import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { DetailedModelMetrics, ModelMetricsPerTarget } from "../../types";
import { InfoTooltip } from "../common/InfoTooltip.tsx";

interface ModelPerformanceChartProps {
  modelData: DetailedModelMetrics | null;
}

// Define available metrics for selection
type SelectableMetric = "CV Mean R²" | "R² (Train)" | "MSE" | "MAE";
const availableMetrics: SelectableMetric[] = [
  "CV Mean R²",
  "R² (Train)",
  "MSE",
  "MAE",
];

// Map internal metric names to selectable names if different
const metricMap: Record<
  SelectableMetric,
  keyof ModelMetricsPerTarget | "cv_mean"
> = {
  "CV Mean R²": "cv_mean", // Map display name to internal key
  "R² (Train)": "r2",
  MSE: "mse",
  MAE: "mae",
};

// Tooltip texts for metrics
const metricExplanations: Record<SelectableMetric, string> = {
  "CV Mean R²":
    "Mittleres Bestimmtheitsmaß (R²) aus der Kreuzvalidierung. Gibt an, wie gut das Modell auf neuen, ungesehenen Daten generalisiert (0-1, höher ist besser).",
  "R² (Train)":
    "Bestimmtheitsmaß (R²) auf den Trainingsdaten. Gibt an, wie gut das Modell die Trainingsdaten erklärt (0-1, höher ist besser). Ein großer Unterschied zum CV R² kann auf Overfitting hindeuten.",
  MSE: "Mean Squared Error (Mittlerer quadratischer Fehler). Durchschnittliche quadratische Abweichung der Vorhersage vom wahren Wert (kleiner ist besser).",
  MAE: "Mean Absolute Error (Mittlerer absoluter Fehler). Durchschnittliche absolute Abweichung der Vorhersage vom wahren Wert (kleiner ist besser).",
};

// Helper to prepare data for the chart based on selected metric
const prepareChartData = (
  metricsPerTarget: DetailedModelMetrics["metrics_per_target"] | undefined,
  selectedMetric: SelectableMetric,
) => {
  if (!metricsPerTarget) return [];

  const internalMetricKey = metricMap[selectedMetric];

  return Object.entries(metricsPerTarget).map(([targetName, metrics]) => {
    const internalMetricKeyTyped = internalMetricKey as keyof Omit<
      ModelMetricsPerTarget,
      "cv_scores"
    >; // Exclude cv_scores
    const rawValue: number | undefined = metrics[internalMetricKeyTyped]; // Explicitly type rawValue as number | undefined
    const value =
      rawValue !== undefined ? parseFloat(rawValue.toFixed(3)) : null;
    return {
      name: targetName,
      value: value, // Use a generic 'value' key for the chart
    };
  });
};

export const ModelPerformanceChart: React.FC<ModelPerformanceChartProps> = ({
  modelData,
}) => {
  // State for the selected metric
  const [selectedMetric, setSelectedMetric] =
    useState<SelectableMetric>("CV Mean R²");

  // Memoize chart data preparation to avoid recalculation on every render
  const chartData = useMemo(() => {
    if (!modelData || !modelData.metrics_per_target) return [];
    return prepareChartData(modelData.metrics_per_target, selectedMetric);
  }, [modelData, selectedMetric]);

  if (!modelData || !modelData.metrics_per_target) {
    // Simplified initial check
    return null; // Or a placeholder card
  }

  // Check if data for the selected metric is available
  const isDataAvailable =
    chartData.length > 0 && chartData.some((d) => d.value !== null);

  const handleMetricChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMetric(event.target.value as SelectableMetric);
  };

  // Determine Y-axis domain based on metric
  const getYAxisDomain = (): [number | string, number | string] => {
    if (selectedMetric === "CV Mean R²" || selectedMetric === "R² (Train)") {
      // R² can be negative, let recharts decide or set specific range e.g., [-1, 1] or ['auto', 'auto']
      return ["auto", "auto"];
    } else {
      // MSE and MAE should be non-negative
      return [0, "auto"];
    }
  };

  return (
    <div className="chart-container card model-performance-chart">
      <div className="chart-header-controls">
        <h3>
          Modell Performance pro Zielvariable
          <InfoTooltip text="Zeigt die Leistung des Modells für jede Zielvariable anhand der ausgewählten Metrik." />
        </h3>
        <div className="metric-selector">
          <label htmlFor="metric-select">
            Metrik:
            <InfoTooltip text={metricExplanations[selectedMetric]} />
          </label>
          <select
            id="metric-select"
            value={selectedMetric}
            onChange={handleMetricChange}
          >
            {availableMetrics.map((metric) => (
              <option key={metric} value={metric}>
                {metric}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isDataAvailable ? (
        <p className="placeholder-message">
          Keine Daten für die Metrik "{selectedMetric}" verfügbar.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{
              top: 25,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              domain={getYAxisDomain()} // Dynamic domain
              label={{
                value: selectedMetric,
                angle: -90,
                position: "insideLeft",
              }}
              tickFormatter={(tick) =>
                typeof tick === "number" ? tick.toFixed(2) : tick
              } // Format Y-axis ticks
            />
            <Tooltip formatter={(value: number) => value.toFixed(3)} />
            <Legend />
            {/* Use generic 'value' key and selectedMetric for the bar name */}
            <Bar dataKey="value" name={selectedMetric} fill="#8884d8">
              <LabelList
                dataKey="value"
                position="top"
                formatter={(value: number) => value.toFixed(2)}
                fontSize={10}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
